import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, TableCell, TableRow } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import { type CSSProperties, useMemo } from 'react';
import classSet from 'react-classset';

type Props = {
  row: any;
  selectedRowId?: string;
  showRowSelection?: boolean;
  rowSelection?: Record<string, boolean>;
  handleRowSelect?: (rowId: string) => void;
  sortable?: boolean;
  rowRefs?: any;
  tableId?: string;
  externalDND?: boolean;
};

const VNTableRow = ({
  row,
  selectedRowId = '',
  showRowSelection = false,
  rowSelection = {},
  handleRowSelect,
  sortable,
  rowRefs,
  tableId,
  externalDND = false,
  ...rest // <-- Captures attributes (like data-id and style templates) passed from getRowProps
}: Props) => {
  const originalId = row.original.id ?? row.id;
  const uniqueId = externalDND ? `${tableId}-${originalId}` : originalId;

  const { transform, transition, setNodeRef, setActivatorNodeRef, isDragging, attributes, listeners } = useSortable({
    id: uniqueId,
    data: {
      tableId,
      row: row.original,
      originalId,
    },
  });

  const isDraggable = Boolean(sortable) || externalDND;

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 9999 : 1,
    position: isDragging ? 'relative' : 'static',
    cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : undefined,
    userSelect: isDraggable ? 'none' : undefined,
    ...rest.style, // Safely mixes incoming custom cursor/styling parameters from the search page layout wrapper
  };

  const isSelected = useMemo(() => row.id === selectedRowId || row.original?.id === selectedRowId, [selectedRowId, row]);
  const renderRowStyle = (row) =>
    classSet({
      'fuel-table-row': true,
      'fuel-table-row--disabled': row.original.isDeleted && row.original.entityType === 'Clip',
      'fuel-table-row--expired': row.original.cssClassForExpired,
      'fuel-table-row--embargoed': row.original.cssClassForEmbargoed,
      'fuel-table-row--isGlobalInterstitialsChannel': row.original.isGlobalInterstitialsChannel,
      'fuel-table-row--parent': row.original.isParentChannel, // For parent row styling if needed
      'hover-row': true,
      highlight: isSelected,
    });
  return (
    <TableRow
      key={row.id}
      className={renderRowStyle(row)}
      style={style}
      // setNodeRef tracks the DOM element for dnd-kit transforms
      // setActivatorNodeRef on the same element means the whole row triggers drag
      ref={(el) => {
        setNodeRef(el);
        if (isDraggable) setActivatorNodeRef(el);
        if (rowRefs && rowRefs.current && row?.original?.id) {
          rowRefs.current[row.original.id] = el;
        }
      }}
      // Spread listeners + attributes only when dragging is enabled
      {...(isDraggable ? { ...attributes, ...listeners } : {})}
      {...rest} // <-- Spreads data-id properties directly into the compiled structural <tr> block element
    >
      {/* Handle icon — visual affordance only, no listeners needed anymore */}
      {isDraggable && (
        <TableCell className="sort-cell">
          <span>
            <FontAwesomeIcon icon={faBars} />
          </span>
        </TableCell>
      )}

      {showRowSelection && (
        <TableCell
          className="checkbox-cell"
          // Stop drag from firing when the user clicks the checkbox
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Checkbox size="small" checked={!!rowSelection[row.id]} onChange={() => handleRowSelect(row.id)} />
        </TableCell>
      )}

      {row.getVisibleCells().map((cell: any) => (
        <TableCell
          key={cell.id}
          sx={{
            textAlign: cell.column.columnDef.meta?.style?.textAlign || 'center',
            // Match the header sizing exactly so column widths stay stable across
            // loading, empty, and populated states.
            width: cell.column.columnDef.size || 'auto',
            minWidth: cell.column.columnDef.size || 'auto',
            maxWidth: cell.column.columnDef.size || 'auto',
            color: row.original.cssClassForEmbargoed ? '#d9534f !important' : 'inherit',
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default VNTableRow;
